import { useState, useEffect } from "react";
import {
  isAuthenticated,
  clearTokens,
  getStoredRealm,
  getRefreshToken,
  getValidToken,
} from "@/integrations/keycloak/client";
import {
  clearLastRealm,
  logoutFromKeycloak,
  markSilentAuthAttempted,
} from "@/config/auth";
import { markExplicitLogout } from "@/utils/postLoginRedirect";
import { resetPosthogUser } from "@/lib/posthog";

interface KeycloakAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
}

/** Intervalo para refresh proativo do access token (antes de expirar). Menor que a vida típica do token (ex.: 5 min). */
const PROACTIVE_REFRESH_INTERVAL_MS = 4 * 60 * 1000; // 4 minutos

/**
 * Hook de autenticação baseado em tokens do Keycloak
 * Verifica se há tokens válidos salvos em memória e faz refresh proativo para evitar logout por inatividade.
 */
export const useKeycloakAuth = (): KeycloakAuthState => {
  const [authState, setAuthState] = useState<KeycloakAuthState>({
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    // Verificar autenticação ao montar
    const checkAuth = () => {
      const authenticated = isAuthenticated();

      setAuthState({
        isAuthenticated: authenticated,
        isLoading: false,
      });
    };

    // Primeira verificação imediata
    checkAuth();

    // Verificar periodicamente (a cada 2 segundos para detectar mudanças)
    const interval = setInterval(checkAuth, 2000);

    // Refresh proativo: renovar access token em background para não deslogar por inatividade.
    // getValidToken() renova se o token estiver expirado ou perto disso; se o refresh falhar, o próximo checkAuth verá isAuthenticated false.
    const refreshInterval = setInterval(() => {
      if (!isAuthenticated()) return;
      getValidToken().catch(() => {
        // Storage já foi limpo pelo getValidToken; checkAuth no outro interval vai atualizar o estado
      });
    }, PROACTIVE_REFRESH_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      clearInterval(refreshInterval);
    };
  }, []);

  return authState;
};

/** Chave do cache persistido do React Query (deve estar em sync com App.tsx / queryPersister) */
const PERSISTED_CACHE_KEY = "MUSA_PORTAL_CACHE";

/**
 * Função para fazer logout completo (limpa tokens locais E revoga no Keycloak)
 */
export const logout = async (): Promise<void> => {
  resetPosthogUser();

  // 1. Obter realm e refresh_token ANTES de limpar (necessário para revogar no Keycloak)
  const realm = getStoredRealm();
  const refreshToken = getRefreshToken();

  // 2. Limpar tokens e cache ANTES do redirect (logoutFromKeycloak redireciona e o código após await não roda)
  clearTokens();
  sessionStorage.clear();
  // Depois do clear, pra sobreviver a ele — ver o comentário na função.
  markExplicitLogout();
  localStorage.removeItem(PERSISTED_CACHE_KEY);
  // Depois do clear, pra sobreviver a ele. Sem estas duas linhas o logout se
  // desfaria: o redirect para o /logout do Keycloak está desativado (ver
  // logoutFromKeycloak), então o cookie de SSO pode continuar de pé — e a
  // reautenticação silenciosa devolveria o usuário logado no instante seguinte
  // ao clique em "Sair". Marcar a tentativa como feita manda o próximo redirect
  // direto pro formulário; esquecer o realm faz esse formulário ser o do
  // generator, senão quem entrou como interno não conseguiria testar com uma
  // conta de gerador.
  markSilentAuthAttempted();
  clearLastRealm();

  // 3. Revogar token no Keycloak e redirecionar para logout (encerra sessão no servidor)
  await logoutFromKeycloak(realm, refreshToken);
};
